import { cn } from "app/lib/utils"
import type { PlayerControlSize } from "app/player/controlSizes"
import { motion, useReducedMotion } from "framer-motion"
import { ComponentProps } from "react"
import { useCompactNavigation } from "web/src/slides/hooks/useCompactNavigation"
import PlayerCircleButton from "./PlayerCircleButton"
import PlayerIcon from "./PlayerIcon"
import VideoHeroPlayButton from "./VideoHeroPlayButton"
import { VIDEO_ROUND_PLAY_ONLY } from "./VideoRoundPlayButton"

const MotionPlayerCircleButton = motion(PlayerCircleButton)

const EXIT_TRANSITION = {
  duration: 0.25,
  ease: "easeOut" as const,
}

type Props = Omit<
  ComponentProps<typeof MotionPlayerCircleButton>,
  "initial" | "animate" | "exit" | "transition" | "children" | "color" | "size"
> & {
  /** Short-height scale (`sm` when player height ≤ 270). */
  size?: PlayerControlSize
}

export default function PlayerInitialPlayButton({
  className,
  onClick,
  size = "md",
  ...props
}: Props) {
  const isCompact = useCompactNavigation()
  const shouldReduceMotion = useReducedMotion()

  if (!isCompact) {
    return (
      <VideoHeroPlayButton
        className={className}
        onClick={onClick}
        size={size}
        {...props}
      />
    )
  }

  const icon = VIDEO_ROUND_PLAY_ONLY[size]

  return (
    <MotionPlayerCircleButton
      color="actie"
      data-id="large-play-button"
      size={size}
      className={className}
      onClick={onClick}
      initial={{ opacity: 1 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: shouldReduceMotion ? 1 : 0 }}
      transition={shouldReduceMotion ? { duration: 0 } : EXIT_TRANSITION}
      {...props}
    >
      <PlayerIcon
        src={icon.src}
        size={icon.size}
        className={cn(icon.className)}
      />
    </MotionPlayerCircleButton>
  )
}
